import { ApplicantType, LinkType, PermitHistoryAction, SyncStatus } from '../constants/permit.constants';
import { I18nString } from './permit-category.model';
import type { FormFieldConfig } from './permit-type.model';

// ─── Populated references ───────────────────────────────────────────────────

export interface PopulatedRef {
    _id: string;
    name: I18nString | string;
}

/**
 * The permit type as populated on a permit. `connected` + `externalId` together mark a type whose
 * workflow is owned by an external system (PSD CityWide): for those, the decision statuses are
 * inbound-only and the backend gates which transitions an agent may perform.
 */
export interface PopulatedPermitTypeRef extends PopulatedRef {
    connected?: boolean;
    externalId?: string;
    // PSD external description (translated), rendered as a subtitle. See permitTypeSubtitle.
    externalDescription?: I18nString;
}

export interface PopulatedStatus {
    _id: string;
    name: I18nString | string;
    color: string;
    type?: string;
    terminal?: boolean;
}

/**
 * One status the agent is allowed to move this permit to, as computed by the backend. Present only
 * on the admin permit-details response — it is per-permit state, so the list response has no
 * equivalent. An empty list is a legitimate state (terminal status, or a PSD-managed one), not an
 * error. Never re-filter this client-side: the gate that produced it depends on how the permit
 * reached its current status, which is not visible from the status alone.
 */
export interface AllowedStatusTransition {
    _id: string;
    type: string; // PermitStatusType enum value
    name: I18nString;
    color: string;
}

export interface PopulatedUser {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
}

// ─── Embedded structures ────────────────────────────────────────────────────

export interface ApplicantInfo {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
}

export interface LocationInfo {
    address?: string;
    coordinates?: { lat: number; lng: number };
    siteLocation?: string;
}

export interface ContractorInfo {
    selfContractor?: boolean;
    name?: string;
    rbq?: string;
    phone?: string;
    email?: string;
}

export interface PermitApplicant {
    type?: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
    user?: string;
}

export interface PermitFee {
    amount?: number;
    description?: string;
    timing: 'upfront' | 'later';
    type?: 'fixed' | 'informational';
    paid: boolean;
    paidAt?: string;
    paymentReference?: string;
}

export interface PermitFile {
    url: string;
    originalName?: string;
    mimeType?: string;
    /** PSD-imported files use `name` instead of `originalName`. */
    name?: string;
    /** PSD-imported permit-type files only. String is legacy (pre-bilingual sync); new syncs write I18nString. */
    description?: I18nString | string;
    createdAt: string;
}

/**
 * Display label for a stored file. Files attached through the temp-upload endpoint carry no
 * `originalName` — only the storage URL comes back — so fall back to the URL's last segment
 * instead of rendering the whole blob URL at the user.
 */
export function permitFileName(file: PermitFile): string {
    if (!file) return '';
    if (file.originalName) return file.originalName;
    if (file.name) return file.name;
    const segment = (file.url || '').split('?')[0].split('/').pop();
    if (!segment) return file.url || '';
    try {
        return decodeURIComponent(segment);
    } catch (_) {
        return segment;
    }
}

/** Localized description for a PSD-imported type file ('' when none). Handles legacy plain-string descriptions too. */
export function permitFileDescription(file: PermitFile, lang: string): string {
    const description = file?.description;
    if (!description) return '';
    return typeof description === 'string' ? description : description[lang] || description.fr || description.en || '';
}

const SAFE_FILE_URL_PROTOCOLS = ['http:', 'https:', 'blob:'];

/** Rejects malformed URLs and non-web schemes (e.g. `javascript:`) before a file URL is opened. */
export function safePermitFileUrl(file: PermitFile): string | null {
    let parsedUrl: URL;
    try {
        parsedUrl = new URL(file?.url, window.location.origin);
    } catch (_error) {
        return null;
    }
    return SAFE_FILE_URL_PROTOCOLS.includes(parsedUrl.protocol) ? parsedUrl.toString() : null;
}

// 'application/octet-stream' is the bciti upload placeholder, not a real mimeType.
export function permitFileRealMimeType(file: PermitFile): string | undefined {
    return file?.mimeType && file.mimeType !== 'application/octet-stream' ? file.mimeType : undefined;
}

export function isPermitFileImage(file: PermitFile): boolean {
    const mimeType = permitFileRealMimeType(file);
    if (mimeType) return mimeType.startsWith('image/');
    return /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff|heic|avif|ico)($|\?)/i.test(file?.url || '');
}

export function isPermitFilePdf(file: PermitFile): boolean {
    const mimeType = permitFileRealMimeType(file);
    if (mimeType) return mimeType === 'application/pdf';
    return /\.pdf($|\?)/i.test(file?.url || '');
}

export function isPermitFileWord(file: PermitFile): boolean {
    const mimeType = permitFileRealMimeType(file);
    if (mimeType) return /wordprocessingml|msword/i.test(mimeType);
    return /\.(doc|docx)($|\?)/i.test(file?.url || '');
}

export function isPermitFileExcel(file: PermitFile): boolean {
    const mimeType = permitFileRealMimeType(file);
    if (mimeType) return /spreadsheetml|ms-excel/i.test(mimeType);
    return /\.(xls|xlsx)($|\?)/i.test(file?.url || '');
}

export interface PermitMessage {
    _id: string;
    sender: string | PopulatedUser;
    senderType: 'citizen' | 'agent';
    text: string;
    files: PermitFile[];
    template?: string;
    createdAt: string;
    readAt?: string;
}

// A threaded internal note (maps to PSD "Internal Comments"). The list replaces the old
// single `internalNote` + separate `internalComments`.
export interface PermitInternalNote {
    _id: string;
    text: string;
    author: string | PopulatedUser;
    createdAt: string;
    edited?: boolean;
    updatedAt?: string;
}

export interface PermitSubtask {
    _id: string;
    title: string;
    assignedTo?: string | PopulatedUser;
    dueDate?: string;
    isDone: boolean;
    completed: boolean;
    completedAt?: string;
    completedBy?: string | PopulatedUser;
    predefined: boolean;
    createdAt: string;
}

export interface PermitLink {
    _id: string;
    type?: string;
    linkType: string;
    linkedPermit: string;
    linkedPermitId?: string;
    createdAt: string;
    createdBy: string;
}

export interface PermitStateChange {
    status: PopulatedStatus;
    changedAt: string;
    changedBy: string | PopulatedUser;
    reason?: string;
    conditions?: string;
}

export interface PermitHistoryEntry {
    _id: string;
    action: PermitHistoryAction;
    actor: string | PopulatedUser;
    timestamp: string;
    metadata?: Record<string, any>;
    description?: string;
}

// ─── Domain models ──────────────────────────────────────────────────────────

export interface AdminPermitListItem {
    _id: string;
    permitId: string;
    permitNumber?: string;
    // External system's human-readable permit number (e.g. PSD CityWide). Optional: absent for
    // bciti-only types and briefly after submission until the sync populates it. Never an identity/key.
    externalPermitNumber?: string;
    category: PopulatedRef;
    permitType: PopulatedPermitTypeRef;
    applicantType: string;
    applicant: PermitApplicant;
    state: PopulatedStatus;
    department: PopulatedRef;
    assignedTo?: PopulatedUser;
    fees: PermitFee[];
    location?: LocationInfo;
    tags: string[];
    syncStatus?: SyncStatus;
    createdAt: string;
    updatedAt: string;
}

export interface AdminPermitDetail extends AdminPermitListItem {
    // The statuses the agent may pick from right now. Detail-only; see AllowedStatusTransition.
    allowedStatusTransitions: AllowedStatusTransition[];
    formData: Record<string, any>;
    // Only the admin-detail endpoint's populate includes these (list endpoints populate 'name' only) —
    // present so this view can resolve real field labels/units instead of raw formData keys.
    permitType: PopulatedPermitTypeRef & { formFields?: FormFieldConfig[]; fieldUnits?: Record<string, I18nString> };
    contractor?: ContractorInfo;
    creator: PopulatedUser;
    files: PermitFile[];
    internalFiles: PermitFile[];
    messages: PermitMessage[];
    internalNotes: PermitInternalNote[];
    subtasks: PermitSubtask[];
    links: PermitLink[];
    stateHistory: PermitStateChange[];
    history: PermitHistoryEntry[];
    validityStartDate?: string;
    validityEndDate?: string;
    expirationNotified: boolean;
    externalId?: string;
    externalMetadata?: Record<string, any>;
    psdUpdatedAt?: string;
    syncedPsdFileIds: string[];
    isDraft: boolean;
    isDeleted: boolean;
    deletedAt?: string;
    sla?: string;
    resolvedAt?: string;
}

export interface PermitCreatedResponse {
    _id: string;
    permitId: string;
    state: PopulatedStatus;
    createdAt: string;
}

// ─── Request payloads ───────────────────────────────────────────────────────

export interface CreatePermitPayload {
    applicantType: ApplicantType;
    applicant?: ApplicantInfo;
    permitType: string;
    formData: Record<string, any>;
    location?: LocationInfo;
    contractor?: ContractorInfo;
    files?: string[];
    paymentToken?: string;
    draftId?: string;
    isDraft?: boolean;
}

export interface AdminCreatePermitPayload extends CreatePermitPayload {
    citizenUserId: string;
}

export interface AdminUpdatePermitPayload {
    formData?: Record<string, any>;
    applicant?: Partial<ApplicantInfo>;
    location?: Partial<LocationInfo>;
    contractor?: Partial<ContractorInfo>;
    assignedTo?: string;
}

export interface UpdateStatusPayload {
    status: string; // status ObjectId
    reason?: string; // required for APPROVED / REFUSED / CONDITIONALLY_APPROVED
    conditions?: string; // required for CONDITIONALLY_APPROVED
}

export interface UpdateDepartmentPayload {
    department: string;
}

export interface UpdateTagsPayload {
    tags: string[];
}

export interface UpdateCategoryAndTypePayload {
    category: string;
    permitType: string;
}

export interface UpdateFeePayload {
    paid: boolean;
    paymentReference?: string;
}

/**
 * A file being attached: the stored URL plus the name it had on the user's machine. The name has
 * to be captured client-side — the temp-upload endpoint returns only a generated blob URL — and
 * sent explicitly, or the permit ends up with a hash for a filename.
 */
export interface PermitFileAttachment {
    url: string;
    originalName: string;
    // The form field this file was uploaded for (file-type fields only). Lets
    // the PSD connector resolve the field's label for the document description.
    fieldId?: string;
}

export interface AttachFilePayload {
    files?: PermitFileAttachment[];
    fileUrl?: string;
}

export type InternalFilePayload = AttachFilePayload;

export interface AdminSendMessagePayload {
    message: string; // field is `message`, not `text`
    // Attached files are sent as objects so the original filename survives. The endpoint still
    // expects the legacy [''] sentinel when nothing is attached, hence the mixed element type.
    attachments?: (string | PermitFileAttachment)[];
    template?: string;
}

export interface AddInternalNotePayload {
    description: string;
}

export interface EditInternalNotePayload {
    description: string;
}

export interface SubtaskItem {
    _id?: string;
    description: string;
    assignedTo?: string;
    dueDate?: string;
    isDone?: boolean;
}

export interface AddSubtasksPayload {
    subtasks: SubtaskItem[];
}

export interface UpdateSubtaskPayload {
    description?: string;
    isDone?: boolean;
    dueDate?: string;
}

export interface AssignSubtaskPayload {
    assignedTo: string | null;
}

export interface CreateLinkPayload {
    type: LinkType;
    linkedPermitId: string;
}

export interface UpdateLinkPayload {
    type: LinkType;
}
